import React from "react";
import { Box, Chip, useTheme } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import Avatar from "../../../features/Booking-Form/assets/avatar.svg";
import Desk from "../../../features/Booking-Form/assets/desk.svg";
import {
  SelectedZoneOrWorkplaceType,
  setIsSelected,
  setSelectedZoneOrWorkplace,
  setSelectedByAttFilter
} from "../../../features/Reports/slices/report.slice";

interface P {
  refetchOnRemove: () => void;
  /** Compact chip for tight headers (e.g. Management floor-plan row). */
  dense?: boolean;
}

const SelectionChips: React.FC<P> = ({ refetchOnRemove, dense = false }) => {
  const theme = useTheme();
  const { selectedZoneOrWorkplace } = useSelector((state: RootState) => state.report);
  const dispatch = useDispatch();

  const handleDeleteSelectZoneOrWp = () => {
    refetchOnRemove();
    dispatch(setIsSelected(false));
    dispatch(
      setSelectedZoneOrWorkplace({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: 0,
        bookingType: undefined
      })
    );
    dispatch(
      setSelectedByAttFilter({
        id: [],
        name: [],
        type: SelectedZoneOrWorkplaceType.ZONE,
        open: false,
        floorInventoryId: -1,
        bookingType: undefined
      })
    );
  };

  if (dense && !selectedZoneOrWorkplace?.open) {
    return null;
  }

  const denseIconFilter =
    theme.palette.mode === "light" ? "brightness(0) opacity(0.54)" : undefined;

  return (
    <Box
      data-testid="report-selection-chip-box"
      sx={{
        marginLeft: dense ? 0 : 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
        listStyle: "none",
        padding: dense ? 0 : theme.spacing(0.5)
      }}
    >
      {selectedZoneOrWorkplace?.open &&
        [selectedZoneOrWorkplace]?.map(data => (
          <li key={uuidv4()}>
            <Chip
              data-testid={"select-chips"}
              size={dense ? "small" : "medium"}
              variant={dense ? "outlined" : "filled"}
              icon={genChipIcon(data.type, dense)}
              label={genChipLabelName(data.name)}
              onDelete={handleDeleteSelectZoneOrWp}
              sx={{
                margin: dense ? 0 : theme.spacing(0.5),
                ...(dense
                  ? {
                      color: "text.primary",
                      "& .MuiChip-deleteIcon": { color: "text.secondary" },
                      /* SVGs are fill=white (Capacity dark chips); tint for outlined light headers */
                      "& .MuiChip-icon": { filter: denseIconFilter }
                    }
                  : {})
              }}
            />
          </li>
        ))}
    </Box>
  );
};

export default SelectionChips;

export function genChipLabelName(name: string[]) {
  const dots = name.length > 1 ? " ..." : "";
  const slicedName = name.length > 1 ? name.join().slice(0, name[0].length) : name;
  const labelName = `${slicedName}${dots}`;
  return labelName;
}

export function genChipIcon(type: SelectedZoneOrWorkplaceType, dense = false) {
  const size = dense ? "16px" : "20px";
  const commonImgProps = {
    width: size,
    height: size,
    style: { marginLeft: dense ? 8 : 10 }
  };

  if (type === SelectedZoneOrWorkplaceType.ZONE)
    return <img {...commonImgProps} src={Desk} alt="logo" />;
  else return <img {...commonImgProps} src={Avatar} alt="logo" />;
}
